<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <div v-if="selectedIds.length" class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <span class="text-paragraph fs-14">{{ t("purchase_page.items_selected", { count: selectedIds.length }) }}</span>
        <button class="btn btn-danger btn-sm" @click="$emit('delete-selected')">
          {{ t("purchase_page.delete_selected") }}
        </button>
      </div>

      <a-table
        :columns="columns"
        :data-source="purchases"
        :row-key="'id'"
        :loading="loading"
        :pagination="pagination"
        :row-selection="rowSelection"
        :scroll="{ x: 1280 }"
        class="text-nowrap align-middle purchase-table"
        @change="handleTableChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("purchase_page.no_purchases_found") }}</div>
        </template>

        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'date'">
            {{ formatDate(record.created_at || record.date) }}
          </template>

          <template v-else-if="column.key === 'status'">
            <span class="badge fs-14 fw-normal text-capitalize" :class="statusClass(record.status)">
              {{ purchaseStatusLabel(record.status) }}
            </span>
          </template>

          <template v-else-if="column.key === 'grand_total'">
            {{ stateStore.formatPrice(Number(record.grand_total || 0)) }}
          </template>

          <template v-else-if="column.key === 'paid'">
            {{ stateStore.formatPrice(Number(record.paid || 0)) }}
          </template>

          <template v-else-if="column.key === 'due'">
            {{ stateStore.formatPrice(getDueAmount(record)) }}
          </template>

          <template v-else-if="column.key === 'payment_status'">
            <span class="badge fs-14 fw-normal text-capitalize payment-status-badge-cell" :class="paymentStatusClass(record.payment_status)">
              {{ paymentStatusLabel(record.payment_status) }}
            </span>
          </template>

          <template v-else-if="column.key === 'paid_by'">
            <span class="text-capitalize paid-by-text" :title="record.payments?.[0]?.method?.name || record.payments?.[0]?.paid_by || t('purchase_page.not_available_short')">
              {{ record.payments?.[0]?.method?.name || record.payments?.[0]?.paid_by || t("purchase_page.not_available_short") }}
            </span>
          </template>

          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button class="btn p-0 border-0 action-icon-btn" :title="t('purchase_page.view')" @click="$emit('view-details', record)">
                <img src="../../../assets/img/icons/eye.svg" :alt="t('purchase_page.view')" style="width: 14px" />
              </button>
              <router-link :to="{ name: 'EditPurchasePage', params: { id: record.id } }" class="btn p-0 border-0 action-icon-btn" :title="t('purchase_page.edit')">
                <img src="../../../assets/img/icons/edit.svg" :alt="t('purchase_page.edit')" style="width: 14px" />
              </router-link>
              <button class="btn p-0 border-0 action-icon-btn" :title="t('purchase_page.delete')" @click="$emit('delete-purchase', record)">
                <img src="../../../assets/img/icons/close.svg" :alt="t('purchase_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>

          <template v-else>
            {{ text || t("purchase_page.not_available_short") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps } from "vue";
import { useI18n } from "vue-i18n";
import type { Purchase } from "@/services/purchase";

const props = defineProps<{
  purchases: Purchase[];
  loading: boolean;
  pagination: Record<string, any>;
  selectedIds: number[];
  formatDate: (date?: string) => string;
  getDueAmount: (purchase: Purchase) => number;
  normalizePaymentStatus: (status?: string) => string;
  normalizePurchaseStatus: (status?: string) => string;
  stateStore: { formatPrice: (price: number) => string };
}>();

const emit = defineEmits<{
  (e: "view-details", purchase: Purchase): void;
  (e: "delete-purchase", purchase: Purchase): void;
  (e: "delete-selected"): void;
  (e: "update:selectedIds", value: number[]): void;
  (e: "table-change", pagination: { current?: number; pageSize?: number }): void;
}>();
const { t } = useI18n();

const selectedRowKeys = computed({
  get: () => props.selectedIds,
  set: (value: (string | number)[]) => {
    emit(
      "update:selectedIds",
      value.map((item) => Number(item)).filter((item) => !Number.isNaN(item))
    );
  },
});

const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  onChange: (newSelectedRowKeys: (string | number)[]) => {
    selectedRowKeys.value = newSelectedRowKeys;
  },
}));

const handleTableChange = (pagination: { current?: number; pageSize?: number }) => {
  emit("table-change", pagination);
};

const paymentStatusLabel = (status?: string) =>
  t(`purchase_page.payment_status_${props.normalizePaymentStatus(status) || "pending"}`);

const purchaseStatusLabel = (status?: string) =>
  t(`purchase_page.purchase_status_${props.normalizePurchaseStatus(status) || "pending"}`);

const paymentStatusClass = (status?: string) => {
  const normalized = props.normalizePaymentStatus(status);

  return {
    "bg-success text-white": normalized === "paid",
    "bg-warning text-dark": normalized === "partial",
    "bg-danger text-white": normalized === "pending",
  };
};

const statusClass = (status?: string) => {
  const normalized = props.normalizePurchaseStatus(status);

  return {
    "bg-success text-white": normalized === "received",
    "bg-warning text-dark": normalized === "ordered" || normalized === "pending",
    "bg-info text-white": normalized === "sent",
    "bg-secondary text-white": !["received", "ordered", "pending", "sent"].includes(normalized),
  };
};

const columns = computed(() => [
  {
    title: t("purchase_page.date"),
    dataIndex: "created_at",
    key: "date",
    align: "center",
    width: 190,
    sorter: (a: Purchase, b: Purchase) =>
      new Date(a.created_at || a.date || "").getTime() - new Date(b.created_at || b.date || "").getTime(),
  },
  {
    title: t("purchase_page.reference"),
    dataIndex: "ref",
    key: "ref",
    align: "center",
    width: 130,
    sorter: (a: Purchase, b: Purchase) => String(a.ref || "").localeCompare(String(b.ref || "")),
  },
  { title: t("purchase_page.supplier"), dataIndex: "supplier_name", key: "supplier_name", align: "center", width: 190, ellipsis: true },
  { title: t("purchase_page.warehouse"), dataIndex: "warehouse_name", key: "warehouse_name", align: "center", width: 170, ellipsis: true },
  { title: t("purchase_page.status"), dataIndex: "status", key: "status", align: "center", width: 130 },
  {
    title: t("purchase_page.grand_total"),
    dataIndex: "grand_total",
    key: "grand_total",
    align: "center",
    width: 140,
    sorter: (a: Purchase, b: Purchase) => Number(a.grand_total || 0) - Number(b.grand_total || 0),
  },
  {
    title: t("purchase_page.paid"),
    dataIndex: "paid",
    key: "paid",
    align: "center",
    width: 120,
    sorter: (a: Purchase, b: Purchase) => Number(a.paid || 0) - Number(b.paid || 0),
  },
  { title: t("purchase_page.due"), key: "due", align: "center", width: 110 },
  { title: t("purchase_page.payment_status"), dataIndex: "payment_status", key: "payment_status", align: "center", width: 150 },
  { title: t("purchase_page.paid_by"), key: "paid_by", align: "center", width: 140, ellipsis: true },
  { title: t("purchase_page.action"), key: "actions", align: "center", width: 90 },
]);
</script>

<style scoped>
.purchase-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.purchase-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.purchase-table :deep(.ant-table-container) {
  overflow: hidden;
}

.paid-by-text {
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.payment-status-badge-cell {
  min-width: 78px;
  display: inline-flex;
  justify-content: center;
}
</style>
